import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, euro, pct, shortDate, TX_LABELS, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowUp,
    ArrowDown,
    Warning,
    CheckCircle,
    Wallet as WalletIcon,
    Bank,
    CreditCard,
    Target,
    Calculator,
    ArrowsClockwise,
    Lock,
} from "@phosphor-icons/react";
import { findConcessionario } from "../config/concessionari";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
    Legend,
} from "recharts";
import SyncProviderPanel from "../components/SyncProviderPanel";

function KPI({ label, value, sub, tone = "default", testid }) {
    const color =
        tone === "positive"
            ? "#00C853"
            : tone === "negative"
              ? "#FF2A00"
              : tone === "brand"
                ? "#002FA7"
                : "#09090b";
    return (
        <div
            className="p-6 bg-white grid-cell hover:bg-zinc-50 transition-colors"
            data-testid={testid}
        >
            <div className="overline mb-3">{label}</div>
            <div className="metric-huge tabular-nums" style={{ color }}>
                {value}
            </div>
            {sub && <div className="text-xs text-zinc-500 mt-2">{sub}</div>}
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    if (user?.role === "operator") return <OperatorDashboard />;
    return <ManagerDashboard />;
}

function ManagerDashboard() {
    const { agencyId, user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [agency, setAgency] = useState(null);
    const [pending, setPending] = useState([]);
    const [posPending, setPosPending] = useState([]);
    const [posBulkLoading, setPosBulkLoading] = useState(false);

    const load = () => {
        if (!agencyId) return;
        setLoading(true);
        api.get(`/dashboard`, { params: { agency_id: agencyId, period: "monthly" } })
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
        api.get("/agencies")
            .then((r) => setAgency(r.data.find((a) => a.id === agencyId) || null))
            .catch(() => {});
        api.get("/closures/pending", { params: { agency_id: agencyId } })
            .then((r) => setPending(r.data))
            .catch(() => {});
        api.get("/transactions/pos/pending", { params: { agency_id: agencyId } })
            .then((r) => setPosPending(r.data))
            .catch(() => {});
    };

    const settleOnePos = async (txId) => {
        try {
            await api.post(`/transactions/${txId}/pos/settle`);
            toast.success("Accredito confermato");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const settleBulkPos = async () => {
        if (posPending.length === 0) return;
        if (!window.confirm(`Confermare l'accredito di ${posPending.length} incassi POS?`)) return;
        setPosBulkLoading(true);
        try {
            const r = await api.post(`/transactions/pos/settle-bulk`, null, {
                params: { agency_id: agencyId },
            });
            toast.success(
                `${r.data.settled_count} incassi accreditati. Commissioni totali: ${euro(r.data.total_fee)}`,
            );
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setPosBulkLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [agencyId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!agencyId)
        return (
            <div className="p-16 text-center">
                <div className="overline mb-4">Nessuna agenzia selezionata</div>
                <Link to="/onboarding" className="btn-primary">
                    Completa l&apos;onboarding
                </Link>
            </div>
        );
    if (loading && !data)
        return <div className="overline p-12">Caricamento…</div>;
    if (!data) return null;

    const fidoPct =
        data.fido_limit_total > 0
            ? Math.min(100, (data.outstanding_total / data.fido_limit_total) * 100)
            : 0;

    const pieData = [
        { name: "Accrediti", value: data.accrediti, color: "#00C853" },
        { name: "Uscite", value: data.uscite, color: "#FF2A00" },
    ].filter((d) => d.value > 0);

    return (
        <div data-testid="dashboard-page">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4 mb-8">
                <div className="w-full lg:w-auto">
                    {(() => {
                        const brand = findConcessionario(agency?.brand_concessionario);
                        return brand ? (
                            <>
                                <img
                                    src={brand.logo}
                                    alt={brand.name}
                                    className="h-16 md:h-20 w-auto mb-3"
                                    data-testid="dashboard-brand-logo"
                                />
                                <div className="overline">Dashboard</div>
                                {agency?.name && (
                                    <div className="text-sm font-bold mt-1">{agency.name}</div>
                                )}
                                {agency?.address && (
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        {agency.address}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="overline mb-2">Control Room</div>
                                <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                                    Dashboard
                                </h1>
                                {agency?.name && (
                                    <div className="text-sm font-bold mt-2">{agency.name}</div>
                                )}
                                {agency?.address && (
                                    <div className="text-xs text-zinc-500 mt-0.5">
                                        {agency.address}
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
                <div className="flex gap-2 flex-wrap items-center w-full lg:w-auto">
                    <Link
                        to="/transactions?new=1"
                        className="btn-primary w-full lg:w-auto text-center"
                        data-testid="new-transaction-btn"
                    >
                        + Nuova transazione
                    </Link>
                </div>
            </div>

            {user?.role === "owner" && agency && agency.apertura_locked !== true && (
                <div
                    className="mb-8 bg-amber-50 border-2 border-amber-400 p-5 flex flex-col sm:flex-row items-start sm:justify-between gap-4"
                    data-testid="apertura-incomplete-banner"
                >
                    <div className="flex items-start gap-3 min-w-0">
                        <Warning size={22} weight="fill" className="mt-0.5 text-amber-600 flex-shrink-0" />
                        <div className="min-w-0">
                            <div className="text-sm font-black tracking-tighter text-amber-900">
                                Apertura saldi non ancora confermata
                            </div>
                            <div className="text-xs text-amber-800 mt-1 leading-relaxed max-w-xl">
                                Per {agency.name}: inserisci i saldi iniziali di cassa,
                                fornitori, clienti e utili. Quando la formula quadra,
                                conferma per blindare i dati contro modifiche accidentali.
                            </div>
                        </div>
                    </div>
                    <Link
                        to="/settings/apertura"
                        className="btn-primary inline-flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-center"
                        data-testid="apertura-cta"
                    >
                        Completa apertura
                    </Link>
                </div>
            )}

            {/* MAIN KPI GRID */}
            {pending && pending.filter((p) => p.n_transactions > 0).length > 0 && (
                <div
                    className="mb-8 bg-amber-50 border-2 border-amber-400 p-5 flex flex-col sm:flex-row items-start sm:justify-between gap-4"
                    data-testid="pending-closures-banner"
                >
                    <div className="flex items-start gap-3 min-w-0">
                        <Warning size={22} weight="fill" className="mt-0.5 text-amber-600 flex-shrink-0" />
                        <div className="min-w-0">
                            <div className="text-sm font-black tracking-tighter text-amber-900">
                                {pending.filter((p) => p.n_transactions > 0).length} mesi con attività da chiudere
                            </div>
                            <div className="text-xs text-amber-800 mt-1 leading-relaxed max-w-xl">
                                Ti consigliamo di chiudere i mesi passati per consolidare il
                                P&L nel wallet Utili Accantonati. Vai alla pagina Chiusure e
                                usa &quot;Chiudi tutti&quot; per farlo in un colpo.
                            </div>
                        </div>
                    </div>
                    <Link
                        to="/closures"
                        className="btn-primary inline-flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-center"
                        data-testid="pending-closures-cta"
                    >
                        Vai a Chiusure
                    </Link>
                </div>
            )}
            {/* KPI GRID ACTUAL */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 grid-room bg-white mb-8">
                <KPI
                    testid="kpi-cassa"
                    label="Cassa Contanti"
                    value={euro(data.cash_total)}
                    sub="Cassa + cassaforte"
                />
                <KPI
                    testid="kpi-banca"
                    label="Saldo Banca"
                    value={euro(data.bank_total)}
                    sub="Conti bancari"
                    tone="brand"
                />
                <KPI
                    testid="kpi-fuori"
                    label="Soldi Fuori"
                    value={euro(data.soldi_fuori)}
                    sub="Crediti ai clienti"
                    tone={data.soldi_fuori > 0 ? "negative" : "default"}
                />
                <KPI
                    testid="kpi-utili"
                    label="Utili Accantonati"
                    value={euro(data.utili_total)}
                    sub="Disponibili per soci"
                    tone="positive"
                />
            </div>

            {/* P&L + Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-3 grid-room bg-white mb-8">
                <KPI
                    testid="kpi-entrate"
                    label="Entrate mese"
                    value={euro(data.entrate)}
                    sub={`Accrediti ${euro(data.accrediti)}`}
                    tone="positive"
                />
                <KPI
                    testid="kpi-uscite"
                    label="Uscite mese"
                    value={euro(data.uscite)}
                    tone="negative"
                />
                <KPI
                    testid="kpi-pnl"
                    label={`P&L · Margine ${pct(data.margin_pct)}`}
                    value={euro(data.pnl)}
                    sub={data.margin_pct != null ? `(P&L / Accrediti) × 100` : "Nessun accredito"}
                    tone={data.pnl >= 0 ? "positive" : "negative"}
                />
            </div>

            {/* Sync rapido fornitori */}
            <div className="mb-8">
                <SyncProviderPanel
                    providers={data.providers}
                    wallets={data.wallets}
                    onDone={load}
                />
            </div>

            {/* Charts + formula */}
            <div className="grid grid-cols-1 lg:grid-cols-2 grid-room bg-white mb-8">
                <div className="p-6 grid-cell">
                    <div className="overline mb-4 flex items-center gap-2">
                        <Target size={14} weight="bold" /> Accrediti vs Uscite
                    </div>
                    {pieData.length === 0 ? (
                        <div className="text-sm text-zinc-500 py-12 text-center">
                            Nessun movimento nel periodo.
                        </div>
                    ) : (
                        <div style={{ width: "100%", height: 280, minHeight: 280 }}>
                            <ResponsiveContainer width="100%" height="100%" minHeight={280}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={90}
                                        innerRadius={55}
                                        paddingAngle={2}
                                        stroke="#fff"
                                        strokeWidth={2}
                                    >
                                        {pieData.map((entry) => (
                                            <Cell key={entry.name} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(v) => euro(v)}
                                        contentStyle={{
                                            background: "#fff",
                                            border: "1px solid #09090b",
                                            borderRadius: 0,
                                            fontFamily: "Chivo",
                                        }}
                                    />
                                    <Legend
                                        verticalAlign="bottom"
                                        iconType="square"
                                        wrapperStyle={{ fontSize: 12, fontWeight: 700 }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="p-6 grid-cell">
                    <div className="overline mb-4 flex items-center gap-2">
                        <Calculator size={14} weight="bold" /> Formula Quadratura
                    </div>
                    <div className="space-y-2 text-sm tabular-nums">
                        <Row label="Fido Totale" value={data.formula.fido_totale} op="+" />
                        <Row
                            label="Accrediti Totali"
                            value={data.formula.accrediti_totali}
                            op="+"
                        />
                        <Row
                            label="Utili Accantonati"
                            value={data.formula.utili_accantonati}
                            op="+"
                        />
                        <Row
                            label="Fondo Cassa"
                            value={data.formula.fondo_cassa}
                            op="+"
                        />
                        <div className="border-t border-zinc-200 pt-3 mt-3 flex justify-between">
                            <span className="overline">Totale Attivi</span>
                            <span className="font-black">{euro(data.formula.attivi)}</span>
                        </div>
                        <Row label="Residuo Fido" value={data.formula.residuo_fido} op="−" />
                        <Row label="Saldo Banca" value={data.formula.saldo_banca} op="−" />
                        <Row label="Sospesi (clienti)" value={data.formula.sospesi} op="−" />
                        <Row label="Spese Totali" value={data.formula.spese_totali} op="−" />
                        <Row label="Cassa Contanti" value={data.formula.cassa_contanti} op="−" />
                        <div className="border-t border-zinc-200 pt-3 mt-3 flex justify-between">
                            <span className="overline">Totale Uscite</span>
                            <span className="font-black">{euro(data.formula.uscite)}</span>
                        </div>
                        <div
                            className="flex justify-between items-center pt-3 border-t border-zinc-200"
                            data-testid="formula-result"
                        >
                            <span className="overline">Scarto</span>
                            {data.formula.quadra ? (
                                <span className="flex items-center gap-2 font-black text-green-600">
                                    <CheckCircle size={16} weight="fill" /> Quadra
                                </span>
                            ) : (
                                <span className="flex items-center gap-2 font-black text-red-600">
                                    <Warning size={16} weight="fill" />
                                    {euro(data.formula.scarto)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Middle band */}
            <div className="grid grid-cols-1 lg:grid-cols-3 grid-room bg-white mb-8">
                <div className="p-6 grid-cell">
                    <div className="overline mb-4 flex items-center gap-2">
                        <Warning size={14} weight="bold" /> Allarmi
                    </div>
                    {data.alerts.length === 0 ? (
                        <div className="flex items-center gap-2 text-sm text-zinc-600">
                            <CheckCircle size={16} weight="bold" className="text-green-600" />
                            Tutto in ordine
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {data.alerts.map((a, i) => (
                                <li
                                    key={`${a.level}:${a.message}`}
                                    className={`text-sm border-l-4 pl-3 ${a.level === "error" ? "border-red-600 text-red-600" : "border-amber-500 text-amber-700"}`}
                                    data-testid={`alert-${i}`}
                                >
                                    {a.message}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="p-6 grid-cell">
                    <div className="overline mb-4 flex items-center gap-2">
                        <WalletIcon size={14} weight="bold" /> Wallet
                    </div>
                    <ul className="space-y-2">
                        {data.wallets.map((w) => (
                            <li
                                key={w.id}
                                className="flex items-center justify-between text-sm"
                            >
                                <span className="flex items-center gap-2">
                                    {w.type === "banca" ? (
                                        <Bank size={14} />
                                    ) : w.type === "cassaforte" ? (
                                        <CreditCard size={14} />
                                    ) : w.type === "utili_accantonati" ? (
                                        <Target size={14} />
                                    ) : (
                                        <WalletIcon size={14} />
                                    )}
                                    <span>{w.name}</span>
                                </span>
                                <span
                                    className="tabular-nums font-bold"
                                    style={{ color: w.balance < 0 ? "#FF2A00" : "#09090b" }}
                                >
                                    {euro(w.balance)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="p-6 grid-cell">
                    <div className="overline mb-4 flex items-center gap-2">
                        <ArrowsClockwise size={14} weight="bold" /> Fido Fornitori
                    </div>
                    <ul className="space-y-4">
                        {data.providers.map((p) => {
                            const pPct =
                                p.fido_limit > 0
                                    ? (p.outstanding_credit / p.fido_limit) * 100
                                    : 0;
                            return (
                                <li key={p.id}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="font-bold">{p.name}</span>
                                        <span className="tabular-nums text-xs text-zinc-600">
                                            {euro(p.fido_limit - p.outstanding_credit)} residuo
                                        </span>
                                    </div>
                                    <div className="h-1 bg-zinc-100">
                                        <div
                                            className="h-full"
                                            style={{
                                                width: `${Math.min(100, pPct)}%`,
                                                background: pPct > 85 ? "#FF2A00" : "#002FA7",
                                            }}
                                        />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="text-xs text-zinc-500 mt-4 pt-3 border-t border-zinc-100">
                        Esposizione totale: <strong>{euro(data.outstanding_total)}</strong>{" "}
                        su {euro(data.fido_limit_total)} ({fidoPct.toFixed(0)}%)
                    </div>
                </div>
            </div>

            {/* POS pending settlement */}
            {posPending.length > 0 && (
                <div
                    className="bg-amber-50 border border-amber-300 mb-8"
                    data-testid="pos-pending-card"
                >
                    <div className="flex items-center justify-between px-6 py-4 border-b border-amber-300">
                        <div>
                            <div className="overline text-amber-900">
                                POS in attesa di accredito
                            </div>
                            <div className="text-xs text-amber-800 mt-1">
                                {posPending.length} incassi POS registrati il giorno X·
                                la banca riceverà il bonifico T+1. Conferma quando arriva.
                            </div>
                        </div>
                        <button
                            onClick={settleBulkPos}
                            disabled={posBulkLoading}
                            className="px-4 py-2 bg-amber-900 text-white text-xs font-bold uppercase tracking-wider hover:bg-amber-950 disabled:opacity-50"
                            data-testid="pos-settle-bulk-btn"
                        >
                            {posBulkLoading ? "Conferma…" : "Conferma tutti gli arrivati"}
                        </button>
                    </div>
                    <table className="ft-table">
                        <thead>
                            <tr>
                                <th>Data registrazione</th>
                                <th>Importo</th>
                                <th>Provider</th>
                                <th className="text-right">Azione</th>
                            </tr>
                        </thead>
                        <tbody>
                            {posPending.slice(0, 10).map((t) => (
                                <tr key={t.id} data-testid={`pos-pending-row-${t.id}`}>
                                    <td className="text-xs">{shortDate(t.occurred_at)}</td>
                                    <td className="font-bold tabular-nums">{euro(t.amount)}</td>
                                    <td className="text-xs">
                                        {data.provider_balances?.find((p) => p.id === t.provider_id)?.name || "—"}
                                    </td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => settleOnePos(t.id)}
                                            className="btn-secondary !py-1.5 !px-3 text-xs"
                                            data-testid={`pos-settle-${t.id}`}
                                        >
                                            ✓ Arrivato
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Recent transactions */}
            <div className="bg-white border border-zinc-200">
                <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
                    <div className="overline">Ultime transazioni</div>
                    <Link
                        to="/transactions"
                        className="text-xs font-bold uppercase tracking-wider hover:underline"
                    >
                        Vedi tutte →
                    </Link>
                </div>
                <table className="ft-table" data-testid="recent-transactions-table">
                    <thead>
                        <tr>
                            <th>Data</th>
                            <th>Tipo</th>
                            <th>Descrizione</th>
                            <th className="text-right">Importo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.recent_transactions.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-center text-zinc-500 py-8">
                                    Nessuna transazione ancora
                                </td>
                            </tr>
                        ) : (
                            data.recent_transactions.map((t) => (
                                <tr key={t.id}>
                                    <td className="text-zinc-600 mono text-xs">
                                        {shortDate(t.occurred_at)}
                                    </td>
                                    <td>
                                        <span className="inline-flex items-center gap-1 text-xs font-bold uppercase">
                                            {[
                                                "entrata",
                                                "commissione",
                                                "vendita_credito",
                                                "accredito",
                                                "rientro_sospeso",
                                                "incasso_pos",
                                            ].includes(t.type) ? (
                                                <ArrowUp size={12} className="text-green-600" />
                                            ) : (
                                                <ArrowDown size={12} className="text-red-600" />
                                            )}
                                            {TX_LABELS[t.type]}
                                        </span>
                                    </td>
                                    <td className="text-zinc-600">
                                        {t.description || t.category || "—"}
                                    </td>
                                    <td className="text-right tabular-nums font-bold">
                                        {euro(t.amount)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Row({ label, value, op }) {
    return (
        <div className="flex justify-between items-baseline">
            <span className="text-zinc-600 text-sm flex items-center gap-2">
                <span
                    className="inline-block w-5 text-center font-black"
                    style={{ color: op === "+" ? "#00C853" : "#FF2A00" }}
                >
                    {op}
                </span>
                {label}
            </span>
            <span className="font-bold tabular-nums">{euro(value)}</span>
        </div>
    );
}

function OperatorDashboard() {
    const { agencyId } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        if (!agencyId) return;
        setLoading(true);
        api.get(`/dashboard/operator`, { params: { agency_id: agencyId } })
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, [agencyId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading || !data) {
        return (
            <div className="space-y-6">
                <div className="overline">Turno in corso</div>
                <div className="text-zinc-500 text-sm">Caricamento…</div>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="operator-dashboard">
            <div>
                <div className="overline mb-2">Operativo</div>
                <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">
                    Il tuo turno.
                </h1>
                <div className="text-sm text-zinc-500 mt-1">
                    Solo i dati che ti servono: cassa, sospesi, sync fornitori.
                </div>
            </div>

            {/* KPI cassa */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                    className="bg-white border border-zinc-200 p-6 md:col-span-2"
                    data-testid="op-kpi-cassa"
                >
                    <div className="overline mb-2">Totale contanti</div>
                    <div className="text-4xl font-black tabular-nums">
                        {euro(data.cash_total)}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        Somma di tutte le casse e cassaforti
                    </div>
                </div>
            </div>

            {/* Wallet cassa breakdown */}
            <div>
                <div className="overline mb-3">Casse</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {data.cash_wallets.map((w) => (
                        <div
                            key={w.id}
                            className="bg-white border border-zinc-200 p-4"
                            data-testid={`op-cash-wallet-${w.id}`}
                        >
                            <div className="overline">{w.type === "cassaforte" ? "Cassaforte" : "Cassa"}</div>
                            <div className="text-sm font-bold mt-1">{w.name}</div>
                            <div className="text-2xl font-black tabular-nums mt-2">
                                {euro(w.balance)}
                            </div>
                        </div>
                    ))}
                    {data.cash_wallets.length === 0 && (
                        <div className="text-sm text-zinc-500">Nessuna cassa configurata.</div>
                    )}
                </div>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Link
                    to="/transactions?new=1"
                    className="block bg-ink text-white p-5 hover:opacity-90 transition-opacity"
                    data-testid="op-quick-new-tx"
                >
                    <div className="overline opacity-70 mb-1">Azione rapida</div>
                    <div className="font-bold text-lg">+ Nuova transazione</div>
                </Link>
                <Link
                    to="/clients"
                    className="block bg-white border border-zinc-300 p-5 hover:border-ink transition-colors"
                    data-testid="op-quick-clients"
                >
                    <div className="overline mb-1">Azione rapida</div>
                    <div className="font-bold text-lg">Lista clienti</div>
                </Link>
            </div>

            {/* Sync provider quick panel */}
            {data.providers && data.providers.length > 0 && (
                <div>
                    <div className="overline mb-3 flex items-center gap-2">
                        <ArrowsClockwise size={12} weight="bold" /> Sync residuo fornitori
                    </div>
                    <div className="text-xs text-zinc-500 mb-3">
                        Leggi il residuo sul portale del fornitore e sincronizza. Il sistema crea
                        automaticamente le vendite credito o le vincite pagate.
                    </div>
                    <SyncProviderPanel
                        providers={data.providers}
                        wallets={data.cash_wallets}
                        onDone={load}
                    />
                </div>
            )}
        </div>
    );
}
